import { EditOutlined } from "@mui/icons-material";
import {
  Accordion,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { getUrl } from "aws-amplify/storage";
import React, { ReactElement, useEffect } from "react";
import styled from "styled-components";
import DefaultLogo from "../assets/images/logo.svg";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme";
import { reportPageWidth } from "../types";
import { convertImageToBase64, formatDate } from "../utils";
import { useReport } from "../contexts/ReportContext";
import { useApp } from "../contexts/AppContext";
import { useApi } from "../contexts/ApiContext";
import Progress from "../components/Progress";
import { useError } from "../contexts/ErrorContext";
import { useNavigate, useParams } from "react-router-dom";
export type ReportType = "report" | "analysis" | "history";

export interface ReportProps {
  title?: string;
  sections: (ReportSectionProps | null)[];
  type: ReportType;
  onIncludeChange?: (sections: { [key: string]: boolean }) => void;
}

interface ReportSectionProps {
  title?: string;
  component: ReactElement;
  showIncludeCheckbox?: boolean;
  showEditButton?: boolean;
  onEditClick?: () => void;
  onIncludeChange?: (checked: boolean) => void;
  checked?: boolean;
  visible?: boolean;
  type?: ReportType;
  id?: string;
}

const StyledReportSection = styled(Stack)(({ theme }) => ({
  "&.edit": {
    border: `1px dashed ${colors.stone[900]}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  border: `1px solid ${colors.stone[300]}`,
  borderRadius: 12,
  backgroundColor: "#ffffff",
  padding: theme.spacing(2),
}));

const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  checked,
  showIncludeCheckbox,
  showEditButton,
  onEditClick,
  onIncludeChange,
  component,
  type,
  visible = true,
  id,
}) => {
  return (
    <StyledReportSection
      id={id}
      sx={{
        display: visible ? "block" : "none",
        border: type === "report" ? "none" : `1px solid ${colors.gray[300]}`,
      }}
      gap={1}
      className={showEditButton || showIncludeCheckbox ? "edit" : ""}
    >
      <Accordion
        sx={{ background: "none" }}
        expanded={checked || !showIncludeCheckbox}
        elevation={0}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {title && (
            <Typography
              fontSize={20}
              color={colors.slate[900]}
              sx={{ marginBottom: 2 }}
              variant="medium"
            >
              {title}
            </Typography>
          )}
          {showIncludeCheckbox && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(event) =>
                    onIncludeChange && onIncludeChange(event.target.checked)
                  }
                />
              }
              label="Include"
            />
          )}
          {showEditButton && (
            <IconButton onClick={() => onEditClick && onEditClick()}>
              <EditOutlined />
            </IconButton>
          )}
        </Stack>

        {component}
      </Accordion>
    </StyledReportSection>
  );
};

const Report: React.FC<ReportProps> = ({
  type,
  title,
  sections,
  onIncludeChange,
}) => {
  const logoRef = React.useRef<HTMLImageElement>(null);
  const { user } = useAuth();
  const { reportEditMode } = useReport();
  const [includedSections, setIncludedSections] = React.useState<{
    [key: string]: boolean;
  }>({});

  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const { translate, currentTool, updateBreadcrumbs } = useApp();
  const { isLoading, setLoading } = useApi();
  const translations = translate("general");

  useEffect(() => {
    if (user?.logo) {
      getUrl({ path: user.logo })
        .then((response) => {
          convertImageToBase64(response.url.toString()).then((base64) => {
            setLogoUrl(base64);
          });
        })
        .catch((error) => {
          console.error("Error getting logo", error);
        });
    }
  }, [user]);

  if (isLoading) {
    return <Progress />;
  }

  return (
    <Stack
      spacing={2}
      mb={4}
      id={"report-container"}
      paddingBottom={15}
      width={reportPageWidth}
      sx={{
        width: "100%",
        margin: "auto",
      }}
    >
      {type === "report" && (
        <Stack
          width={"100%"}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <img
            id={"report-logo"}
            onError={() => setLogoUrl(DefaultLogo)}
            ref={logoRef}
            height={32}
            src={logoUrl || DefaultLogo}
          />
          <Box>
            <Typography variant="report.date">
              {translations.report.dateOfTest}: {formatDate(new Date())}
            </Typography>
          </Box>
        </Stack>
      )}
      <Typography fontSize={24} variant="medium">
        {title}
      </Typography>
      <Stack
        spacing={2}
        sx={{ backgroundColor: type === "report" ? "white" : "transparent" }}
      >
        {sections
          .filter((s): s is ReportSectionProps => s !== null)
          .map((section, index) => (
            <ReportSection
              onIncludeChange={(checked) => {
                if (!section.id) {
                  return;
                }
                setIncludedSections({
                  ...includedSections,
                  [section.id]: checked,
                });
              }}
              id={`section-${section.id ?? index}`}
              showIncludeCheckbox={!!section.id && reportEditMode}
              key={index}
              type={type}
              checked={!!section.id && includedSections[section.id]}
              {...section}
            />
          ))}
      </Stack>
    </Stack>
  );
};

export default Report;
